import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Button, Select, Input, Checkbox, Upload, Switch } from 'antd';
import styles from './SingleCreateUsers.styles';
import { colors, zIndex } from '../../../theme/theme';
import { useIntl } from 'react-intl';
import SelectorLayout from '../../shared/SelectorLayout';
import { PlusOutlined } from '@ant-design/icons';
import { selectAdminBrands } from '../../../redux/store/userData/user';
import { createUser, updateUser } from '../../../redux/store/userData/users';
import {
  closeModal,
  getIsVisibleModal,
  selectUserWithUIEffect,
} from '../../../redux/store/ui/common';
import ListItem from '../../shared/ListItem';
import ListEditItem from '../../shared/ListEditItem';

const { Option } = Select;

enum LEGAL_TYPES {
  AG = 'AG',
  EV = 'e.V.',
  GmbH = 'GmbH',
  CoKG = 'GmbH & Co KG',
  KG = 'KG',
  OHG = 'OHG',
  Sonstige = 'Sonstige',
}

const SingleCreateUsers = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const brands = useSelector(selectAdminBrands);
  const visible = useSelector(getIsVisibleModal('UsersCreateModal'));

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [userType, setUserType] = useState<string>('Individual');
  const [brandId, setBrandId] = useState<string>(
    brands.length < 2 ? brands[0].id : ''
  );
  const [companyName, setCompanyName] = useState<string>('');
  const [legalType, setLegalType] = useState('');

  const [transactionalNotifications, setTransactionalNotifications] =
    useState(false);

  const SUPPORTED_USERS_TYPES = ['Individual', 'Freelancer'];

  const isCompany = userType === 'Company';
  const isFreelancer = userType === 'Freelancer';

  const billingProfile = isFreelancer ? 'business' : null;

  const clearCreateUserModal = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setBrandId(brands.length < 2 ? brands[0].id : '');
    setUserType('Individual');
    setCompanyName('');
    setLegalType('');
    setTransactionalNotifications(false);
  };

  const handleOnSubmit = (e: any) => {
    e.preventDefault();

    const newUser = {
      email,
      firstName,
      lastName,
      password,
      brandId,
      userType: isCompany ? 'org' : 'user',
      ...(companyName ? { companyName } : {}),
      ...(legalType ? { legalType } : {}),
      ...(billingProfile
        ? { address: { billingProfile: billingProfile } }
        : {}),
      notifications: {
        transactionalMessages: transactionalNotifications,
      },
    };

    batch(() => {
      dispatch(
        createUser(newUser, (createdUser) => {
          dispatch(selectUserWithUIEffect(createdUser));
          dispatch(closeModal('UsersCreateModal'));
          clearCreateUserModal();
        })
      );
    });
  };

  const handleOnCancel = () => {
    dispatch(closeModal('UsersCreateModal'));
    clearCreateUserModal();
  };

  const isDisabled =
    !firstName ||
    !lastName ||
    !lastName ||
    !email ||
    !password ||
    !userType ||
    (isCompany && !companyName) ||
    (isCompany && !legalType);

  useEffect(() => {
    clearCreateUserModal();
  }, [visible]);

  return (
    <>
      <div>
        <div className="create-user-contents" tabIndex={0}>
          <>
            <div
              style={{
                marginBottom: 15,
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ width: '49%' }}>
                <Input
                  style={{ width: '100%' }}
                  placeholder={intl.formatMessage({
                    id: 'fleetadmin.admins.info.firstName',
                  })}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>

              <div style={{ width: '49%' }}>
                <Input
                  style={{ width: '100%' }}
                  placeholder={intl.formatMessage({
                    id: 'fleetadmin.admins.info.lastName',
                  })}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Input
                type="email"
                style={{ marginBottom: 8 }}
                placeholder={intl.formatMessage({
                  id: 'fleetadmin.admins.info.email',
                })}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <Input
                type="password"
                style={{ marginBottom: 8 }}
                placeholder={intl.formatMessage({
                  id: 'fleetadmin.user.create.password.placeholder',
                })}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </>

          <div className="add-user-subtitle">
            {intl.formatMessage({
              id: `fleetadmin.admins.permission.user.title`,
            })}
          </div>

          {brands?.length >= 2 ? (
            <Select
              className="selector"
              value={brandId || undefined}
              style={{ width: '100%', marginBottom: 8 }}
              placeholder="Select Brands"
              dropdownStyle={{ zIndex: zIndex.dropDown }}
              onChange={(value: string) => {
                setBrandId(value);
              }}
            >
              {brands.map((brand) => (
                <Option key={`type-option-${brand.name}`} value={brand.id}>
                  {brand?.name}
                </Option>
              ))}
            </Select>
          ) : (
            <Select
              className="selector"
              value={brands[0].name}
              style={{ width: '100%', marginBottom: 8 }}
              placeholder={intl.formatMessage({
                id: 'fleetadmin.user.create.brand.placeholder',
              })}
              dropdownStyle={{ zIndex: zIndex.dropDown }}
              disabled={true}
            />
          )}

          <Select
            className="selector"
            value={userType || undefined}
            placeholder="User Type"
            style={{
              width: '100%',
              marginBottom: 8,
            }}
            dropdownStyle={{ zIndex: zIndex.dropDown }}
            onChange={(value: string) => {
              setUserType(value);
            }}
          >
            {SUPPORTED_USERS_TYPES.map((type) => (
              <Option key={`type-option-${type}`} value={type}>
                {intl.formatMessage({
                  id: `fleetadmin.user.create.type.${type}`,
                })}
              </Option>
            ))}
          </Select>

          {userType && userType !== 'Individual' && (
            <Input
              type="company Name"
              style={{ marginBottom: 8 }}
              placeholder={intl.formatMessage({
                id: 'fleetadmin.user.create.company.name.placeholder',
              })}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          )}
          {isCompany && (
            <>
              <Select
                className="selector"
                value={legalType || undefined}
                style={{ width: '100%' }}
                placeholder={intl.formatMessage({
                  id: `fleetadmin.users.info.legaltype`,
                })}
                onChange={(code: string) => setLegalType(code)}
                dropdownStyle={{ zIndex: zIndex.dropDown }}
              >
                {Object.values(LEGAL_TYPES)?.map((typ) => (
                  <Option key={`area-option-${typ}`} value={typ}>
                    {typ}
                  </Option>
                ))}
              </Select>
            </>
          )}

          <div className="add-user-subtitle">
            {intl.formatMessage({
              id: `fleetadmin.users.status.notifications`,
            })}
          </div>
          <div className="user-messages" style={{ margin: 0, marginTop: 8 }}>
            <ListEditItem
              style={{ marginLeft: 0, marginRight: 0 }}
              leftItem={
                <div className="permission-label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.notification.transactional`,
                  })}
                </div>
              }
              rightItem={
                <Switch
                  checked={transactionalNotifications}
                  onChange={() => {
                    setTransactionalNotifications(!transactionalNotifications);
                  }}
                />
              }
            />
          </div>

          <div className="user-submit-button-container">
            <Button
              className="submit-button"
              style={isDisabled ? { background: colors.buttonDisabled } : {}}
              onClick={handleOnSubmit}
              disabled={isDisabled}
            >
              {intl.formatMessage({
                id: `submit`,
              })}
            </Button>

            <Button
              className="cancel-button"
              style={{ width: '100%', marginTop: 16 }}
              onClick={handleOnCancel}
            >
              {intl.formatMessage({
                id: `cancel`,
              })}
            </Button>
          </div>
        </div>
      </div>
      <style jsx global>
        {styles}
      </style>
    </>
  );
};

export default SingleCreateUsers;
